import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Grid, Paper, Stack, Typography, Chip, MenuItem, Select, FormControl,
  InputLabel, TextField, IconButton, Tooltip, LinearProgress, Alert, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
} from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../api/axios';
import { useDeviceSelection } from '../context/DeviceSelectionContext';
import { useAuth } from '../auth/AuthContext';
import StatTile from './dashboard/StatTile';
import { ActivityOverTime, OutcomeDonut, RankBar } from './analytics/charts';
import DrillDialog from './analytics/DrillDialog';

dayjs.extend(relativeTime);

const TIMEFRAMES = [
  { label: '7 days', value: 7 },
  { label: '15 days', value: 15 },
  { label: '30 days', value: 30 },
  { label: '6 months', value: 180 },
  { label: 'Custom', value: 'custom' },
];

const fmt = (n) => (n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString());
const rel = (t) => (t ? dayjs(t).fromNow() : '—');
const fmtTime = (t) => (t ? dayjs(t).format('MMM D, YYYY HH:mm:ss') : '—');

const hasOutcome = (log) =>
  log.action === 'Device Feedback' ||
  log.action === 'Data ACK' ||
  (log.deviceResponse && typeof log.deviceResponse.success === 'boolean');

const isSuccess = (log) => {
  if (log.action === 'Device Feedback') return true;
  if (log.deviceResponse && typeof log.deviceResponse.success === 'boolean') return log.deviceResponse.success;
  if (log.action === 'Data ACK' && log.parameters) {
    if (typeof log.parameters.ack === 'boolean') return log.parameters.ack;
    if (typeof log.parameters.status === 'number') return log.parameters.status === 0;
  }
  return false;
};

const isTest = (log) => log.action === 'Send SW Parameters';

// Column definitions for the drill-down / export tables.
const COLS = {
  testLog: [
    { id: 'device', label: 'Device', mono: true }, { id: 'location', label: 'Location' },
    { id: 'elements', label: 'Elements', align: 'right' }, { id: 'quantity', label: 'Total Qty', align: 'right' },
    { id: 'user', label: 'User' }, { id: 'time', label: 'Time' },
  ],
  // Business view: no command/outcome internals (those are superadmin-only).
  deviceBusiness: [
    { id: 'device', label: 'Device', mono: true }, { id: 'name', label: 'Name' }, { id: 'location', label: 'Location' },
    { id: 'tests', label: 'Tests', align: 'right' }, { id: 'last', label: 'Last activity' },
  ],
  // Superadmin view: adds command volume + success rate.
  deviceTable: [
    { id: 'device', label: 'Device', mono: true }, { id: 'name', label: 'Name' }, { id: 'location', label: 'Location' },
    { id: 'tests', label: 'Tests', align: 'right' }, { id: 'commands', label: 'Commands', align: 'right' },
    { id: 'success', label: 'Success', align: 'right' }, { id: 'last', label: 'Last activity' },
  ],
  elementTable: [
    { id: 'element', label: 'Element' }, { id: 'uses', label: 'Times used', align: 'right' },
    { id: 'quantity', label: 'Total quantity', align: 'right' },
  ],
  elementUsage: [
    { id: 'device', label: 'Device', mono: true }, { id: 'quantity', label: 'Quantity', align: 'right' }, { id: 'time', label: 'Time' },
  ],
  commandLog: [
    { id: 'device', label: 'Device', mono: true }, { id: 'action', label: 'Action' }, { id: 'transport', label: 'Transport' },
    { id: 'outcome', label: 'Outcome' }, { id: 'user', label: 'User' }, { id: 'time', label: 'Time' },
  ],
  outcomeLog: [
    { id: 'device', label: 'Device', mono: true }, { id: 'action', label: 'Action' },
    { id: 'outcome', label: 'Outcome' }, { id: 'message', label: 'Message' }, { id: 'time', label: 'Time' },
  ],
  series: [{ id: 'date', label: 'Date' }, { id: 'commands', label: 'Commands', align: 'right' }],
  actionCount: [{ id: 'action', label: 'Action' }, { id: 'count', label: 'Count', align: 'right' }],
};

const SectionCard = ({ title, action, children, onDrill, sx }) => (
  <Paper onClick={onDrill}
    sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column',
      ...(onDrill && { cursor: 'pointer', transition: 'box-shadow .2s', '&:hover': { boxShadow: 4 } }), ...sx }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {action}
        {onDrill && <Tooltip title="View & export as table"><TableChartOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} /></Tooltip>}
      </Stack>
    </Stack>
    {children}
  </Paper>
);

const AnalyticsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [regDevices, setRegDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState(30);
  const [customStart, setCustomStart] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [customEnd, setCustomEnd] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedElement, setSelectedElement] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [drill, setDrill] = useState(null);
  const { selectedDeviceId, setSelectedDeviceId } = useDeviceSelection();
  const auth = useAuth() || {};
  const role = auth.role || sessionStorage.getItem('role') || 'user';
  const isAdmin = ['admin', 'superadmin'].includes(role);
  const isSuperadmin = role === 'superadmin';

  const range = useMemo(() => {
    if (timeframe === 'custom' && customStart && customEnd) {
      return { start: dayjs(customStart).startOf('day'), end: dayjs(customEnd).endOf('day') };
    }
    const days = typeof timeframe === 'number' ? timeframe : 30;
    return { start: dayjs().subtract(days, 'day').startOf('day'), end: dayjs().endOf('day') };
  }, [timeframe, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('startDate', range.start.toISOString());
      params.append('endDate', range.end.toISOString());
      params.append('limit', '2000');
      if (selectedDeviceId) params.append('deviceId', selectedDeviceId);
      const endpoint = isAdmin ? '/history/all' : '/history/user';
      const res = await api.get(`${endpoint}?${params.toString()}`);
      setLogs(Array.isArray(res.data) ? res.data : []);
      setGeneratedAt(new Date().toISOString());
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load insights');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [range, selectedDeviceId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const loadDevices = useCallback(async () => {
    try {
      const res = await api.get('/device/devices/registered');
      const list = Array.isArray(res.data) ? res.data : [];
      setRegDevices(list);
      return list;
    } catch { setRegDevices([]); return []; }
  }, []);

  useEffect(() => {
    const timers = [];
    loadDevices().then((list) => {
      // Coordinates may still be reverse-geocoding on the backend; refetch a couple
      // of times to pick up freshly-resolved "area, city" labels.
      const pending = list.some((x) => x.latitude != null && x.longitude != null && !x.locationName);
      if (pending) { timers.push(setTimeout(loadDevices, 4000)); timers.push(setTimeout(loadDevices, 10000)); }
    });
    return () => timers.forEach(clearTimeout);
  }, [loadDevices]);

  const regMap = useMemo(() => {
    const m = {};
    regDevices.forEach((d) => {
      const hasCoords = d.latitude != null && d.longitude != null;
      const coords = hasCoords ? `${Number(d.latitude).toFixed(4)}, ${Number(d.longitude).toFixed(4)}` : null;
      m[d.deviceId] = {
        name: d.customName || d.deviceId,
        locLabel: d.locationName || coords || '—', // prefer "area, city", fall back to coords
        locKey: d.locationName || coords || null,
        hasLoc: hasCoords,
      };
    });
    return m;
  }, [regDevices]);

  const nameOf = useCallback((id) => regMap[id]?.name || id, [regMap]);
  const locOf = useCallback((id) => regMap[id]?.locLabel || '—', [regMap]);

  // --- All derived insights + drill/export row sets, computed from the logs ---
  const d = useMemo(() => {
    const deviceOptions = Array.from(new Set([
      ...regDevices.map((x) => x.deviceId),
    ])).map((id) => ({ id, name: regMap[id]?.name || id }));

    const outcomeLogs = logs.filter(hasOutcome);
    const success = outcomeLogs.filter(isSuccess).length;
    const failed = outcomeLogs.length - success;
    const tests = logs.filter(isTest);
    const elementsInTests = tests.reduce((a, l) => a + (Array.isArray(l.elements) ? l.elements.length : (l.elementCount || 0)), 0);
    const totalQuantity = logs.reduce((a, l) => a + (Array.isArray(l.elements) ? l.elements.reduce((s, e) => s + (typeof e.quantity === 'number' ? e.quantity : 0), 0) : 0), 0);

    // Per-day series (all commands, and tests only).
    const cmdByDay = {}; const testByDay = {};
    logs.forEach((l) => { const k = dayjs(l.timestamp).format('YYYY-MM-DD'); cmdByDay[k] = (cmdByDay[k] || 0) + 1; });
    tests.forEach((l) => { const k = dayjs(l.timestamp).format('YYYY-MM-DD'); testByDay[k] = (testByDay[k] || 0) + 1; });
    const spanDays = Math.min(Math.max(range.end.diff(range.start, 'day') + 1, 1), 185);
    const mkSeries = (byDay) => Array.from({ length: spanDays }, (_, i) => {
      const day = range.end.subtract(spanDays - 1 - i, 'day').format('YYYY-MM-DD');
      return { day, count: byDay[day] || 0 };
    });
    const cmdSeries = mkSeries(cmdByDay);
    const testSeries = mkSeries(testByDay);

    // Action mix.
    const actionCounts = {};
    logs.forEach((l) => { const a = l.action || 'Unknown'; actionCounts[a] = (actionCounts[a] || 0) + 1; });
    const actionMix = Object.entries(actionCounts).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([label, value]) => ({ label, value }));

    // Elements.
    const elemCounts = {}; const elemQty = {};
    logs.forEach((l) => (Array.isArray(l.elements) ? l.elements : []).forEach((e) => {
      if (!e || !e.symbol) return;
      elemCounts[e.symbol] = (elemCounts[e.symbol] || 0) + 1;
      if (typeof e.quantity === 'number') elemQty[e.symbol] = (elemQty[e.symbol] || 0) + e.quantity;
    }));
    const elementList = Object.keys(elemCounts).sort();
    const elemByUses = Object.entries(elemCounts).sort((a, b) => b[1] - a[1]);
    const topElements = elemByUses.slice(0, 8).map(([label, value]) => ({ label, value }));
    const topDosing = Object.entries(elemQty).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([label, value]) => ({ label, value: Math.round(value) }));

    // Per-device rollup.
    const dev = {};
    logs.forEach((l) => {
      const id = l.deviceId || l.device_id || 'unknown';
      if (!dev[id]) dev[id] = { id, commands: 0, tests: 0, outcomes: 0, success: 0, last: null };
      dev[id].commands += 1;
      if (isTest(l)) dev[id].tests += 1;
      if (hasOutcome(l)) { dev[id].outcomes += 1; if (isSuccess(l)) dev[id].success += 1; }
      if (l.timestamp && (!dev[id].last || dayjs(l.timestamp).isAfter(dev[id].last))) dev[id].last = l.timestamp;
    });
    const deviceRows = Object.values(dev).sort((a, b) => b.commands - a.commands);
    const locations = new Set(deviceRows.map((x) => regMap[x.id]?.locKey).filter(Boolean)).size;
    const operators = new Set(logs.map((l) => l.userId || l.user_id).filter(Boolean)).size;

    // ---- Export/drill row sets (plain objects) ----
    const testRows = tests.map((l) => ({
      device: nameOf(l.deviceId || l.device_id), location: locOf(l.deviceId || l.device_id),
      elements: Array.isArray(l.elements) ? l.elements.length : (l.elementCount || 0),
      quantity: Array.isArray(l.elements) ? Math.round(l.elements.reduce((s, e) => s + (e.quantity || 0), 0)) : (l.totalQuantity || 0),
      user: l.user?.username || l.userId || '—', time: fmtTime(l.timestamp),
    }));
    const deviceTableRows = deviceRows.map((x) => ({
      device: x.id, name: nameOf(x.id), location: locOf(x.id), tests: x.tests, commands: x.commands,
      success: x.outcomes ? `${Math.round((x.success / x.outcomes) * 100)}%` : '—', last: rel(x.last),
    }));
    const elementUsesRows = elemByUses.map(([sym, uses]) => ({ element: sym, uses, quantity: Math.round(elemQty[sym] || 0) }));
    const elementDosingRows = [...elementUsesRows].sort((a, b) => b.quantity - a.quantity);
    const outcomeRows = outcomeLogs.map((l) => ({
      device: nameOf(l.deviceId || l.device_id), action: l.action,
      outcome: isSuccess(l) ? 'Success' : 'Failed',
      message: l.deviceResponse?.message || l.parameters?.message || '', time: fmtTime(l.timestamp),
    }));
    const commandRows = logs.map((l) => ({
      device: nameOf(l.deviceId || l.device_id), action: l.action, transport: l.transportMode || l.transport_mode || 'auto',
      outcome: hasOutcome(l) ? (isSuccess(l) ? 'Success' : 'Failed') : '—',
      user: l.user?.username || l.userId || '—', time: fmtTime(l.timestamp),
    }));
    const cmdSeriesRows = cmdSeries.map((s) => ({ date: dayjs(s.day).format('MMM D, YYYY'), commands: s.count }));
    const testSeriesRows = testSeries.map((s) => ({ date: dayjs(s.day).format('MMM D, YYYY'), commands: s.count }));
    const actionRows = Object.entries(actionCounts).sort((a, b) => b[1] - a[1]).map(([action, count]) => ({ action, count }));

    const elementInfo = selectedElement ? {
      used: elemCounts[selectedElement] || 0,
      quantity: Math.round(elemQty[selectedElement] || 0),
      share: (() => { const tot = elemByUses.reduce((a, [, v]) => a + v, 0); return tot ? Math.round(((elemCounts[selectedElement] || 0) / tot) * 100) : 0; })(),
      rows: logs.filter((l) => (l.elements || []).some((e) => e.symbol === selectedElement)).map((l) => {
        const e = (l.elements || []).find((x) => x.symbol === selectedElement);
        return { device: nameOf(l.deviceId || l.device_id), quantity: e && typeof e.quantity === 'number' ? e.quantity : 0, time: fmtTime(l.timestamp) };
      }),
    } : null;

    return {
      deviceOptions,
      total: logs.length, success, failed,
      successRate: outcomeLogs.length ? Math.round((success / outcomeLogs.length) * 100) : null,
      tests: tests.length, avgElementsPerTest: tests.length ? elementsInTests / tests.length : 0,
      totalQuantity: Math.round(totalQuantity), activeDevices: deviceRows.length, locations, operators,
      cmdSeries, testSeries, actionMix, topElements, topDosing, elementList, elementInfo,
      testRows, deviceTableRows, elementUsesRows, elementDosingRows, outcomeRows, commandRows,
      cmdSeriesRows, testSeriesRows, actionRows,
    };
  }, [logs, range, selectedElement, regMap, regDevices, nameOf, locOf]);

  const openDrill = (descriptor) => setDrill(descriptor);
  const tfLabel = TIMEFRAMES.find((t) => t.value === timeframe)?.label || 'range';

  const rateAccent = d.successRate == null ? 'text.secondary'
    : d.successRate >= 90 ? 'success.main' : d.successRate >= 70 ? 'warning.main' : 'error.main';

  // Business KPI tiles (visible to everyone), each opens a drill table.
  const businessTiles = [
    { label: 'Tests', value: fmt(d.tests), icon: <ScienceOutlinedIcon fontSize="small" />, sub: 'SW parameter runs', drill: { title: 'Tests', subtitle: tfLabel, columns: COLS.testLog, rows: d.testRows } },
    { label: 'Active devices', value: fmt(d.activeDevices), icon: <DevicesOtherOutlinedIcon fontSize="small" />, sub: 'reported in range', drill: { title: 'Devices & locations', subtitle: tfLabel, columns: COLS.deviceBusiness, rows: d.deviceTableRows } },
    { label: 'Locations', value: fmt(d.locations), icon: <PlaceOutlinedIcon fontSize="small" />, sub: 'distinct sites', drill: { title: 'Devices & locations', subtitle: tfLabel, columns: COLS.deviceBusiness, rows: d.deviceTableRows } },
    { label: 'Avg elements / test', value: d.tests ? d.avgElementsPerTest.toFixed(1) : '—', icon: <CategoryOutlinedIcon fontSize="small" />, sub: 'per run', drill: { title: 'Tests', subtitle: tfLabel, columns: COLS.testLog, rows: d.testRows } },
    { label: 'Total quantity', value: fmt(d.totalQuantity), icon: <OpacityOutlinedIcon fontSize="small" />, sub: 'dosed in range', drill: { title: 'Element dosing', subtitle: tfLabel, columns: COLS.elementTable, rows: d.elementDosingRows } },
    ...(isAdmin ? [{ label: 'Operators', value: fmt(d.operators), icon: <PeopleAltOutlinedIcon fontSize="small" />, sub: 'unique users' }] : []),
  ];

  return (
    <Box>
      {/* Sub-header + filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {selectedDeviceId ? `Device ${selectedDeviceId}` : 'All devices'} · updated {rel(generatedAt)} · click any card to export
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Device</InputLabel>
          <Select value={selectedDeviceId || ''} label="Device" onChange={(e) => setSelectedDeviceId(e.target.value)}>
            <MenuItem value=""><em>All devices</em></MenuItem>
            {d.deviceOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select value={timeframe} label="Timeframe" onChange={(e) => setTimeframe(e.target.value)}>
            {TIMEFRAMES.map((tf) => <MenuItem key={tf.value} value={tf.value}>{tf.label}</MenuItem>)}
          </Select>
        </FormControl>
        {timeframe === 'custom' && (
          <>
            <TextField type="date" size="small" label="Start" value={customStart} onChange={(e) => setCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            <TextField type="date" size="small" label="End" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          </>
        )}
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><RefreshOutlinedIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ===================== BUSINESS INSIGHTS (all roles) ===================== */}
      <Grid container spacing={2}>
        {businessTiles.map((t) => (
          <Grid item xs={6} sm={4} md={2} key={t.label}>
            <Box onClick={t.drill ? () => openDrill(t.drill) : undefined} sx={{ height: '100%', ...(t.drill && { cursor: 'pointer' }) }}>
              <StatTile label={t.label} value={t.value} sub={t.sub} icon={t.icon} accent={t.accent} />
            </Box>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={8}>
          <SectionCard title="Tests over time" action={<Typography variant="caption" color="text.secondary">per day</Typography>}
            onDrill={() => openDrill({ title: 'Tests over time', subtitle: 'Daily test runs', columns: COLS.series, rows: d.testSeriesRows })}>
            <Box sx={{ height: 260 }}><ActivityOverTime series={d.testSeries} /></Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Most used elements" action={<Typography variant="caption" color="text.secondary">by selection</Typography>}
            onDrill={() => openDrill({ title: 'Element usage', subtitle: tfLabel, columns: COLS.elementTable, rows: d.elementUsesRows })}>
            <Box sx={{ height: 260 }}><RankBar rows={d.topElements} unit="uses" /></Box>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Element dosing" action={<Typography variant="caption" color="text.secondary">total quantity</Typography>}
            onDrill={() => openDrill({ title: 'Element dosing', subtitle: tfLabel, columns: COLS.elementTable, rows: d.elementDosingRows })}>
            <Box sx={{ height: 260 }}><RankBar rows={d.topDosing} unit="qty" /></Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Element explorer">
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select element</InputLabel>
              <Select value={selectedElement} label="Select element" onChange={(e) => setSelectedElement(e.target.value)}
                renderValue={(v) => v || 'Choose an element'} onClick={(e) => e.stopPropagation()}>
                <MenuItem value=""><em>Choose an element</em></MenuItem>
                {d.elementList.map((el) => <MenuItem key={el} value={el}>{el}</MenuItem>)}
              </Select>
            </FormControl>
            {d.elementInfo ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Times used</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{fmt(d.elementInfo.used)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Total quantity</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{fmt(d.elementInfo.quantity)}</Typography>
                  </Box>
                </Stack>
                <Divider />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    <b>{selectedElement}</b> ≈ <b>{d.elementInfo.share}%</b> of element selections in range.
                  </Typography>
                  <Chip size="small" variant="outlined" icon={<TableChartOutlinedIcon />} label="Export"
                    onClick={() => openDrill({ title: `Element: ${selectedElement}`, subtitle: 'Usage records', columns: COLS.elementUsage, rows: d.elementInfo.rows })} />
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Pick an element to see how often it's used, its total dosed quantity, and export the usage records.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12}>
          <SectionCard title="Devices & locations" action={<Chip size="small" variant="outlined" label={`${d.deviceTableRows.length} devices`} />}
            onDrill={() => openDrill({ title: 'Devices & locations', subtitle: tfLabel, columns: COLS.deviceBusiness, rows: d.deviceTableRows })}>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell><TableCell>Location</TableCell>
                    <TableCell align="right">Tests</TableCell>
                    <TableCell align="right">Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.deviceTableRows.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No activity in this range</TableCell></TableRow>
                  )}
                  {d.deviceTableRows.slice(0, 12).map((r) => (
                    <TableRow key={r.device} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{r.device}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{r.location}</Typography></TableCell>
                      <TableCell align="right">{fmt(r.tests)}</TableCell>
                      <TableCell align="right"><Typography variant="caption" color="text.secondary">{r.last}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ============= COMMAND & API INSIGHTS (superadmin only) ============= */}
      {isSuperadmin && (
        <>
          <Divider sx={{ my: 3 }}>
            <Chip icon={<LockOutlinedIcon />} label="Command & API insights · Superadmin" size="small" color="primary" variant="outlined" />
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Box onClick={() => openDrill({ title: 'All commands', subtitle: tfLabel, columns: COLS.commandLog, rows: d.commandRows })} sx={{ height: '100%', cursor: 'pointer' }}>
                <StatTile label="Commands" value={fmt(d.total)} icon={<BoltOutlinedIcon fontSize="small" />} sub={tfLabel} />
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box onClick={() => openDrill({ title: 'Command outcomes', subtitle: 'Success / failed', columns: COLS.outcomeLog, rows: d.outcomeRows })} sx={{ height: '100%', cursor: 'pointer' }}>
                <StatTile label="Success rate" value={d.successRate == null ? '—' : `${d.successRate}%`} icon={<CheckCircleOutlineIcon fontSize="small" />} sub={`${fmt(d.success)} ok · ${fmt(d.failed)} failed`} accent={rateAccent} />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={8}>
              <SectionCard title="Command activity" action={<Typography variant="caption" color="text.secondary">per day</Typography>}
                onDrill={() => openDrill({ title: 'Command activity', subtitle: 'Daily command volume', columns: COLS.series, rows: d.cmdSeriesRows })}>
                <Box sx={{ height: 260 }}><ActivityOverTime series={d.cmdSeries} /></Box>
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <SectionCard title="Command outcomes"
                onDrill={() => openDrill({ title: 'Command outcomes', subtitle: 'Success / failed', columns: COLS.outcomeLog, rows: d.outcomeRows })}>
                <Box sx={{ height: 260 }}><OutcomeDonut success={d.success} failed={d.failed} /></Box>
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <SectionCard title="Action mix"
                onDrill={() => openDrill({ title: 'Action mix', subtitle: tfLabel, columns: COLS.actionCount, rows: d.actionRows })}>
                <Box sx={{ height: 260 }}><RankBar rows={d.actionMix} unit="commands" /></Box>
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionCard title="Command reliability by device" action={<Chip size="small" variant="outlined" label={`${d.deviceTableRows.length} devices`} />}
                onDrill={() => openDrill({ title: 'Command reliability by device', subtitle: tfLabel, columns: COLS.deviceTable, rows: d.deviceTableRows })}>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Device</TableCell><TableCell align="right">Commands</TableCell><TableCell align="right">Success</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.deviceTableRows.slice(0, 10).map((r) => (
                        <TableRow key={r.device} hover>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.device}</Typography></TableCell>
                          <TableCell align="right">{fmt(r.commands)}</TableCell>
                          <TableCell align="right">{r.success}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </Grid>
          </Grid>
        </>
      )}

      <DrillDialog open={!!drill} onClose={() => setDrill(null)}
        title={drill?.title} subtitle={drill?.subtitle} columns={drill?.columns || []} rows={drill?.rows || []} />
    </Box>
  );
};

export default AnalyticsPanel;
